// src/TimeTracking.js
import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  Chip,
  Divider,
  Tooltip,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/system';
import jsPDF from 'jspdf';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ReplayIcon from '@mui/icons-material/Replay';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import { serverTimestamp } from 'firebase/firestore';

import { useAuth } from './AuthContext';
import { useAccess } from './useAccess';
import {
  getAllItems,
  addItem,
  updateItem,
  deleteItem,
  loadUserSingleDoc,
  saveUserSingleDoc,
  getItemsPage,
} from './firestore';

// ---------- Premium buttons ----------
const PrimaryButton = styled(Button)({
  borderRadius: 12,
  padding: '10px 16px',
  textTransform: 'none',
  fontWeight: 900,
  boxShadow: 'none',
  backgroundColor: '#000',
  color: '#fff',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#222',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
  },
  '&:disabled': { backgroundColor: '#111', color: 'rgba(255,255,255,0.6)' },
});

const SubtleButton = styled(Button)({
  borderRadius: 12,
  padding: '10px 16px',
  textTransform: 'none',
  fontWeight: 900,
  boxShadow: 'none',
  backgroundColor: '#fff',
  color: '#000',
  border: '1px solid rgba(0,0,0,0.15)',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#fafafa',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
  },
});

const TinyButton = styled(Button)({
  borderRadius: 999,
  padding: '6px 10px',
  textTransform: 'none',
  fontWeight: 800,
});

const StyledSelect = styled(Select)({
  backgroundColor: '#ffffff',
  color: '#000000',
  '& .MuiSelect-icon': { color: '#000000' },
});

const MobilePopper = styled(Popper)(({ theme }) => ({
  zIndex: 1500,

  [theme.breakpoints.down('sm')]: {
    width: 'calc(100vw - 32px) !important',
    maxWidth: 'calc(100vw - 32px) !important',
  },
}));


const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = String(dateStr).split('-');
  if (!year || !month || !day) return String(dateStr);
  return `${day}/${month}/${year}`;
};

const todayISO = () => new Date().toISOString().split('T')[0];

const monthStartISO = (d = new Date()) => {
  const x = new Date(d);
  x.setDate(1);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addMonths = (iso, delta) => {
  const d = new Date(iso + 'T00:00:00');
  d.setMonth(d.getMonth() + delta);
  return d.toISOString().split('T')[0];
};

const monthsAgoMonthStartISO = (monthsAgo = 2) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - monthsAgo);
  return monthStartISO(d);
};

const monthLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleString(undefined, { month: 'short', year: 'numeric' });
};

const calculateDueDateISO = () => {
  const today = new Date();
  const due = new Date(today);
  due.setDate(today.getDate() + 14);
  return due.toISOString().split('T')[0];
};

const codeShort = (full) => (full ? String(full).split(' - ')[0] : '');
const codeDesc = (full) => (full ? String(full).split(' - ').slice(1).join(' - ') : '');
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const TRAVEL_CODE_SHORT = '01_799_0107_1_1';
const TRAVEL_CODE = `${TRAVEL_CODE_SHORT} - Provider travel - non-labour costs`;
const TEMPLATE_COLLECTION = 'timeTemplates';

// ---- 5-minute time options (00:00 to 23:55) ----
const buildTimeOptions5 = () => {
  const out = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 5) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      out.push(`${hh}:${mm}`);
    }
  }
  return out;
};
const TIME_OPTIONS_5 = buildTimeOptions5();

const SoftCard = ({ title, subtitle, icon, right, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5 },
      mb: 2.5,
      borderRadius: 4,
      border: '1px solid rgba(0,0,0,0.08)',
      bgcolor: '#fff',
      color: '#000',
      boxShadow: '0 18px 45px rgba(0,0,0,0.05)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 999,
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'rgba(0,0,0,0.06)',
          color: '#000',
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 240, color: '#000' }}>
        <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1, color: '#000' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)', mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {right}
    </Box>

    <Divider sx={{ my: 2 }} />
    {children}
  </Paper>
);

const clampText = (s, max = 90) => {
  const t = String(s || '').trim();
  if (!t) return '—';
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
};

const getMonthKey = (iso) => String(iso || '').slice(0, 7);

// --------------------
// Billing Codes (EXACTLY like Invoices.js)
// --------------------
const makeISOId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const toLineString = (c) => {
  const code = String(c?.code || '').trim();
  const desc = String(c?.description || '').trim();
  return desc ? `${code} - ${desc}` : code;
};

const normalizeCodes = (maybe) => {
  const arr = Array.isArray(maybe) ? maybe : [];
  if (arr.length === 0) return [];

  if (typeof arr[0] === 'string') {
    return arr
      .map((s) => {
        const full = String(s || '');
        const code = codeShort(full);
        const description = codeDesc(full);
        return { id: makeISOId(), code, description, defaultRate: '', favorite: false };
      })
      .filter((x) => x.code);
  }

  return arr
    .map((x) => ({
      id: x?.id || makeISOId(),
      code: String(x?.code || '').trim(),
      description: String(x?.description || '').trim(),
      defaultRate: x?.defaultRate ?? '',
      favorite: !!x?.favorite,
    }))
    .filter((x) => x.code);
};

const defaultBillingCodes = [
  {
    id: 'seed-1',
    code: '01_011_0107_1_1',
    description: 'Assistance With Self-Care Activities - Standard - Weekday Daytime',
    defaultRate: '',
    favorite: true,
  },
  {
    id: 'seed-2',
    code: '01_015_0107_1_1',
    description: 'Assistance With Self-Care Activities - Standard - Weekday Evening',
    defaultRate: '',
    favorite: true,
  },
  {
    id: 'seed-3',
    code: '01_002_0107_1_1',
    description: 'Assistance With Self-Care Activities - Standard - Weekday Night',
    defaultRate: '',
    favorite: false,
  },
  {
    id: 'seed-4',
    code: '01_400_0104_1_1',
    description: 'Assistance With Self-Care Activities - High Intensity - Weekday Daytime',
    defaultRate: '',
    favorite: false,
  },
  {
    id: 'seed-5',
    code: TRAVEL_CODE_SHORT,
    description: 'Provider travel - non-labour costs',
    defaultRate: '',
    favorite: false,
  },
];

const TimeTracking = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { canUsePaidFeatures, loading: accessLoading } = useAccess();
  const isLocked = !canUsePaidFeatures;
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches;


  // toast
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const showToast = (severity, message) => setToast({ open: true, severity, message });

  const runIfUnlocked = (fn, msg = 'Preview mode — actions are locked. Start a free trial to use this feature.') => {
    if (isLocked) {
      showToast('info', msg);
      return;
    }
    fn();
  };

  // ✅ paged shifts
  const [shifts, setShifts] = useState([]);
  const [participants, setParticipants] = useState([]);

  // templates (per user)
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');

  // settings + user info
  const [settings, setSettings] = useState({ nextInvoiceId: 1 });
  const [userInfo, setUserInfo] = useState({});

  // ✅ Billing codes (user-defined, persisted like Invoices.js)
  const [myCodes, setMyCodes] = useState(defaultBillingCodes);
  const [codesLoaded, setCodesLoaded] = useState(false);

  // codes dialog
  const [codesOpen, setCodesOpen] = useState(false);
  const [codesDraft, setCodesDraft] = useState([]);
  const [codesQuery, setCodesQuery] = useState('');

  // selection for invoice creation
  const [selectedShiftIds, setSelectedShiftIds] = useState([]);

  // shift form
  const [participantId, setParticipantId] = useState('');
  const [date, setDate] = useState(todayISO());
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [notes, setNotes] = useState('');
  // ✅ Notes cursor insertion (autofills insert where caret is)
const notesRef = useRef(null);
const notesSelRef = useRef({ start: 0, end: 0 });

const captureNotesSelection = () => {
  const el = notesRef.current;
  if (!el) return;
  notesSelRef.current = {
    start: el.selectionStart ?? 0,
    end: el.selectionEnd ?? 0,
  };
};

const insertIntoNotesAtCursor = (insertText) => {
  setNotes((prev) => {
    const value = prev || '';
    const { start, end } = notesSelRef.current || { start: value.length, end: value.length };

    const safeStart = Number.isFinite(start) ? start : value.length;
    const safeEnd = Number.isFinite(end) ? end : value.length;

    const before = value.slice(0, safeStart);
    const after = value.slice(safeEnd);

    // nice spacing if you insert mid-text
    const needsLeadingGap = before && !before.endsWith('\n') ? '\n\n' : '';
    const next = `${before}${needsLeadingGap}${insertText}${after}`;

    // restore caret after inserted text
    queueMicrotask(() => {
      const el = notesRef.current;
      if (!el) return;
      el.focus();
      const pos = (before + needsLeadingGap + insertText).length;
      el.setSelectionRange(pos, pos);
      notesSelRef.current = { start: pos, end: pos };
    });

    return next;
  });
};


  // ✅ Autofills UI
  const [showAutofills, setShowAutofills] = useState(false);

  // billing per shift
  const [supportItemCode, setSupportItemCode] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [travelKm, setTravelKm] = useState('');
  const [travelRatePerKm, setTravelRatePerKm] = useState('');

  const [editingId, setEditingId] = useState(null);

  // base loading + shifts loading
  const [loadingBase, setLoadingBase] = useState(true);
  const [loadingShifts, setLoadingShifts] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // pagination cursor
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // server-side range (default: this month)
  const [rangeMode, setRangeMode] = useState('month'); // month | threeMonths | all
  const [rangeFrom, setRangeFrom] = useState(monthStartISO());
  const [rangeTo, setRangeTo] = useState(addMonths(monthStartISO(), 1)); // exclusive upper bound

  // filters (client-side on loaded window)
  const [showDrafts, setShowDrafts] = useState(true);
  const [showInvoiced, setShowInvoiced] = useState(true);
  const [filterParticipantId, setFilterParticipantId] = useState('');

  // template dialogs
  const [openSaveTemplate, setOpenSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [openManageTemplates, setOpenManageTemplates] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [editingTemplateName, setEditingTemplateName] = useState('');

  // Dropdown menu fix - white background, black text
  const menuProps = useMemo(
  () => ({
    // ✅ key fix for iOS zoom: don't portal to <body>
    disablePortal: true,

    // ✅ prevents weird mount/unmount jumps
    keepMounted: true,

    // ✅ stops scroll-lock weirdness on iOS
    disableScrollLock: true,

    // ✅ stable anchoring
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },

    PaperProps: {
      sx: {
        bgcolor: '#ffffff',
        color: '#000000',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 2,
        mt: 0.5,

        // ✅ mobile-friendly sizing
        width: { xs: 'calc(100vw - 32px)', sm: 420 },
        maxWidth: { xs: 'calc(100vw - 32px)', sm: 420 },
        maxHeight: 420,

        '& .MuiMenuItem-root': {
          color: '#000000',
          whiteSpace: 'normal',
          alignItems: 'flex-start',
          lineHeight: 1.2,
          py: 1,
          '&:hover': { backgroundColor: '#f5f5f5' },
          '&.Mui-selected': { backgroundColor: '#e0e0e0', '&:hover': { backgroundColor: '#d0d0d0' } },
        },
      },
    },

    MenuListProps: {
      dense: true,
      sx: { py: 0.5 },
    },
  }),
  []
);



  // ✅ Autofills (kept)
  const autofillOptions = [
    'Assisted participant with personal hygiene including showering, grooming and dressing.',
    'Provided toileting support and maintained dignity and privacy throughout.',
    'Supported continence care as per care plan and completed hygiene tasks afterwards.',
    'Supported participant with meal preparation and eating (prompting and assistance as required).',
    'Assisted with meal planning, grocery list preparation and safe food handling.',
    'Assisted with light household tasks (cleaning, laundry, dishes) to maintain a safe environment.',
    'Administered/prompted medication as per care plan and documented as required.',
    'Supported participant with bedtime routine and settling for rest/sleep.',
    'Monitored wellbeing and safety throughout the shift; responded to needs as required.',
    'Supported participant with community access / outing (walk, shopping, appointment).',
    'Provided transport support to/from appointment and assisted with safe community mobility.',
    'Attended appointment with participant and supported communication/advocacy as requested.',
    'Provided emotional support and engaged in meaningful conversation to build rapport.',
    'Supported participant with social interaction and communication (prompting and encouragement).',
    'Encouraged and assisted with daily exercises/therapy activities as per plan.',
    'Implemented agreed behaviour support strategies and used calm communication to support regulation.',
    'Identified early warning signs and used de-escalation strategies; maintained safety for participant and others.',
    'Supported participant to make choices and take breaks as needed; followed care plan guidance.',
    'Completed shift documentation and communicated key information/observations to relevant parties.',
    'Provided handover summary at end of shift including supports provided and any notable observations.',
  ];

  const autofillLabels = [
    'Hygiene',
    'Toileting',
    'Continence',
    'Meals',
    'Meal Plan',
    'Household',
    'Medication',
    'Bedtime',
    'Safety',
    'Outing',
    'Transport',
    'Appointment',
    'Emotional',
    'Social',
    'Exercise',
    'Behaviour Plan',
    'De-escalation',
    'Choices/Breaks',
    'Documentation',
    'Handover',
  ];

  const autofillNotes = (text) => {
  insertIntoNotesAtCursor(text);
};

  const clearNotes = () => setNotes('');

  const activeParticipants = useMemo(
    () =>
      (participants || [])
        .filter((p) => !p.archived)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [participants]
  );

  const calculateHours = () => {
    if (!startTime || !endTime) return '0.00';

    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '0.00';

    // overnight ok
    if (end < start) end.setDate(end.getDate() + 1);

    const diff = (end - start) / (1000 * 60 * 60);
    if (diff <= 0) return '0.00';

    return diff.toFixed(2);
  };

  const hoursValue = Number(calculateHours());

  const templateHeader =
    `Shift Summary (Daily Shift Report)\n` +
    `Location:\n` +
    `Support Activities Provided:\n` +
    `Goals/Outcomes:\n` +
    `Participant Presentation (mood/engagement):\n` +
    `Risks/Incidents (if any):\n` +
    `Handover / Next Steps:\n`;

  const insertTemplateHeader = () => {
    setNotes((prev) => {
      const trimmed = (prev || '').trim();
      if (trimmed.startsWith('Shift Summary')) return prev;
      return trimmed ? `${templateHeader}\n${trimmed}` : templateHeader;
    });
  };

  const resetForm = () => {
    setParticipantId('');
    setDate(todayISO());
    setStartTime('');
    setEndTime('');
    setNotes('');
    setSupportItemCode('');
    setUnitPrice('');
    setTravelKm('');
    setTravelRatePerKm('');
    setEditingId(null);
    setShowAutofills(false);
    setTemplateId('');
  };

  // ------------------------
  // ✅ Server-side shifts loading (paged + range)
  // ------------------------
  const buildShiftFilters = useCallback(() => {
    if (rangeMode === 'all') return [];
    return [
      { field: 'date', op: '>=', value: rangeFrom },
      { field: 'date', op: '<', value: rangeTo },
    ];
  }, [rangeMode, rangeFrom, rangeTo]);

  const loadFirstPageShifts = useCallback(async () => {
    if (!currentUser) return;
    setLoadingShifts(true);
    try {
      const { items, lastDoc: ld, hasMore: hm } = await getItemsPage({
        uid: currentUser.uid,
        collectionName: 'timeEntries',
        filters: buildShiftFilters(),
        order: { field: 'date', dir: 'desc' },
        pageSize: 50,
        cursor: null,
      });

      setShifts(items || []);
      setLastDoc(ld || null);
      setHasMore(!!hm);

      setSelectedShiftIds((prev) => prev.filter((id) => (items || []).some((x) => x.id === id)));
    } catch (e) {
      console.error('Load shifts failed:', e);
      showToast('error', 'Failed to load shift reports.');
      setShifts([]);
      setLastDoc(null);
      setHasMore(false);
      setSelectedShiftIds([]);
    } finally {
      setLoadingShifts(false);
    }
  }, [currentUser, buildShiftFilters]);

  const loadMoreShifts = async () => {
    if (!currentUser || !hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const { items, lastDoc: ld, hasMore: hm } = await getItemsPage({
        uid: currentUser.uid,
        collectionName: 'timeEntries',
        filters: buildShiftFilters(),
        order: { field: 'date', dir: 'desc' },
        pageSize: 50,
        cursor: lastDoc,
      });

      setShifts((prev) => [...(prev || []), ...(items || [])]);
      setLastDoc(ld || lastDoc);
      setHasMore(!!hm);
    } catch (e) {
      console.error('Load more shifts failed:', e);
      showToast('error', 'Failed to load more shift reports.');
    } finally {
      setLoadingMore(false);
    }
  };

  // Range controls
  const setThisMonth = () => {
    const from = monthStartISO(new Date());
    setRangeMode('month');
    setRangeFrom(from);
    setRangeTo(addMonths(from, 1));
  };

  const setLast3Months = () => {
    const from = monthsAgoMonthStartISO(2);
    const endNextMonth = addMonths(monthStartISO(new Date()), 1);
    setRangeMode('threeMonths');
    setRangeFrom(from);
    setRangeTo(endNextMonth);
  };

  const setAllTime = () => {
    setRangeMode('all');
    const from = monthStartISO(new Date());
    setRangeFrom(from);
    setRangeTo(addMonths(from, 1));
  };

  const rangeChipLabel =
    rangeMode === 'all'
      ? 'Range: All time'
      : rangeMode === 'threeMonths'
      ? `Range: Last 3 months (${monthLabel(rangeFrom)} → ${monthLabel(addMonths(rangeTo, -1))})`
      : `Range: ${monthLabel(rangeFrom)}`;

  // ------------------------
  // initial load (participants/userInfo/settings/templates + codes) + first shifts page
  // ------------------------
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const loadBase = async () => {
      setLoadingBase(true);
      try {
        const [loadedParticipants, loadedSettings, loadedUserInfo, loadedTemplates] = await Promise.all([
          getAllItems(currentUser.uid, 'participants'),
          loadUserSingleDoc(currentUser.uid, 'settings', { nextInvoiceId: 1 }),
          loadUserSingleDoc(currentUser.uid, 'userInfo', {}),
          getAllItems(currentUser.uid, TEMPLATE_COLLECTION),
        ]);

        setParticipants(loadedParticipants || []);
        setSettings(loadedSettings || { nextInvoiceId: 1 });
        setUserInfo(loadedUserInfo || {});
        setTemplates((loadedTemplates || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')));

        // ✅ load billing codes from settings (or seed defaults) — same as Invoices.js
        const normalized = normalizeCodes(loadedSettings?.billingCodes);
        const finalCodes = normalized.length ? normalized : defaultBillingCodes;
        setMyCodes(finalCodes);
        setCodesLoaded(true);

        // seed once if none
        if (!normalized.length) {
          await saveUserSingleDoc(currentUser.uid, 'settings', {
            ...(loadedSettings || { nextInvoiceId: 1 }),
            billingCodes: finalCodes,
            updatedAt: serverTimestamp(),
          });
        }
      } catch (error) {
        console.error('Error loading base data:', error);
        showToast('error', 'Failed to load participants/settings/templates.');
      } finally {
        setLoadingBase(false);
      }
    };

    loadBase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, navigate]);

  // reload shifts whenever the range changes
  useEffect(() => {
    if (!currentUser) return;
    loadFirstPageShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, rangeMode, rangeFrom, rangeTo]);

  // ---- Copy last shift into the form (across ALL TIME, server-side) ----
  const copyLastShiftToForm = async () => {
    if (!currentUser) return;
    try {
      const { items } = await getItemsPage({
        uid: currentUser.uid,
        collectionName: 'timeEntries',
        filters: [],
        order: { field: 'date', dir: 'desc' },
        pageSize: 1,
        cursor: null,
      });

      const last = (items || [])[0];
      if (!last) {
        showToast('warning', 'No previous shifts found to copy.');
        return;
      }

      setParticipantId(last.participantId || '');
      setDate(todayISO());
      setStartTime(last.startTime || '');
      setEndTime(last.endTime || '');
      setNotes(last.notes || '');
      setSupportItemCode(last.supportItemCode || '');
      setUnitPrice(last.unitPrice ?? '');
      setTravelKm(last.travelKm ?? '');
      setTravelRatePerKm(last.travelRatePerKm ?? '');
      setEditingId(null);
      setShowAutofills(false);
      setTemplateId('');

      showToast('success', 'Copied your last shift into the form (not saved yet).');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      showToast('error', 'Failed to copy last shift: ' + e.message);
    }
  };

  // ------------------------
  // Billing codes ordering: favourites first, then alphabetically
  // ------------------------
  const myCodesSorted = useMemo(() => {
    const list = (myCodes || []).slice();
    list.sort((a, b) => {
      const fa = a.favorite ? 1 : 0;
      const fb = b.favorite ? 1 : 0;
      if (fa !== fb) return fb - fa;
      return String(a.code || '').localeCompare(String(b.code || ''));
    });
    return list;
  }, [myCodes]);

  const supportCodeOptions = useMemo(
    () => (myCodesSorted || []).filter((c) => String(c.code || '').trim() !== TRAVEL_CODE_SHORT),
    [myCodesSorted]
  );

  const travelCodeObj = useMemo(
    () => (myCodesSorted || []).find((c) => String(c.code || '').trim() === TRAVEL_CODE_SHORT) || null,
    [myCodesSorted]
  );

  const selectedSupportCodeObj = useMemo(() => {
    const full = String(supportItemCode || '');
    if (!full) return null;
    const short = codeShort(full);
    return (supportCodeOptions || []).find((c) => String(c.code || '') === String(short || '')) || null;
  }, [supportItemCode, supportCodeOptions]);

  const onSelectSupportCodeObj = (objOrNull) => {
    if (isLocked) return;

    const full = objOrNull ? toLineString(objOrNull) : '';
    setSupportItemCode(full);

    // Autofill unit price if empty + defaultRate exists (same behavior as Invoices.js)
    const hasPrice = Number(unitPrice || 0) > 0;
    const rate = Number(objOrNull?.defaultRate || 0);
    if (!hasPrice && rate > 0) setUnitPrice(String(rate));
  };

  const onSetTravelDefaultsIfAvailable = () => {
    const hasTravelRate = Number(travelRatePerKm || 0) > 0;
    const rate = Number(travelCodeObj?.defaultRate || 0);
    if (!hasTravelRate && rate > 0) setTravelRatePerKm(String(rate));
  };

  // ------------------------
  // Billing Codes dialog (same as Invoices.js)
  // ------------------------
  const saveMyCodes = async (codes) => {
    const cleaned = (codes || [])
      .map((c) => ({
        id: c.id || makeISOId(),
        code: String(c.code || '').trim(),
        description: String(c.description || '').trim(),
        defaultRate: c.defaultRate ?? '',
        favorite: !!c.favorite,
      }))
      .filter((c) => c.code);

    const final = cleaned.length ? cleaned : defaultBillingCodes;
    setMyCodes(final);

    await saveUserSingleDoc(currentUser.uid, 'settings', {
      ...(settings || { nextInvoiceId: 1 }),
      billingCodes: final,
      updatedAt: serverTimestamp(),
    });
  };

  const openCodes = () => {
    if (isLocked) return showToast('info', 'Preview mode — actions are locked.');
    setCodesDraft((myCodes || []).map((c) => ({ ...c })));
    setCodesQuery('');
    setCodesOpen(true);
  };

  const addCodeRow = () => {
    setCodesDraft((prev) => [
      { id: makeISOId(), code: '', description: '', defaultRate: '', favorite: false },
      ...(prev || []),
    ]);
  };

  const updateCodeRow = (id, patch) => {
    setCodesDraft((prev) => (prev || []).map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const deleteCodeRow = (id) => {
    setCodesDraft((prev) => (prev || []).filter((c) => c.id !== id));
  };

  const saveCodesDialog = async () => {
    try {
      await saveMyCodes(codesDraft);
      setCodesOpen(false);
      showToast('success', 'Billing codes saved.');
    } catch (e) {
      console.error('Save codes failed:', e);
      showToast('error', `Failed to save codes: ${e.message}`);
    }
  };

  const filteredCodesDraft = useMemo(() => {
    const q = codesQuery.trim().toLowerCase();
    const list = codesDraft || [];
    if (!q) return list;
    return list.filter((c) => {
      const code = String(c.code || '').toLowerCase();
      const desc = String(c.description || '').toLowerCase();
      return code.includes(q) || desc.includes(q);
    });
  }, [codesDraft, codesQuery]);

  // ------------------------
  // Save / Edit / Delete shifts
  // ------------------------
  const handleSave = async () => {
    if (isLocked) {
      showToast('info', 'Preview mode — actions are locked. Start a free trial to save shifts.');
      return;
    }

    if (!participantId || !date || !startTime || !endTime || !notes.trim()) {
      showToast('warning', 'Please fill in participant, date, start/end time, and shift notes.');
      return;
    }

    if (!hoursValue || hoursValue <= 0) {
      showToast('warning', 'Please enter a valid start and end time (hours must be > 0).');
      return;
    }

    // If support code selected, require unit price
    if (supportItemCode && (unitPrice === '' || Number(unitPrice) <= 0)) {
      showToast('warning', 'Please enter a Unit Price (or clear the Support Item Code).');
      return;
    }

    // If travel km, require rate
    const kmNum = Number(travelKm || 0);
    const rateNum = Number(travelRatePerKm || 0);
    if (kmNum > 0 && (!travelRatePerKm || rateNum <= 0)) {
      showToast('warning', 'Please enter a Travel Rate ($/km) if Travel KM is provided.');
      return;
    }

    const participant = participants.find((p) => p.id === participantId);

    const shiftData = {
      participantId,
      date,
      startTime,
      endTime,
      hours: hoursValue.toFixed(2),
      notes: notes.trim(),

      supportItemCode: supportItemCode || '',
      unitPrice: unitPrice !== '' ? Number(unitPrice) : null,

      travelKm: travelKm !== '' ? Number(travelKm) : null,
      travelRatePerKm: travelRatePerKm !== '' ? Number(travelRatePerKm) : null,

      invoiced: false,
      invoiceId: null,
      invoiceNumber: null,

      participantSnapshot: {
        name: participant?.name || 'Unknown',
        ndisNumber: participant?.ndisNumber || 'Unknown',
      },

      updatedAt: serverTimestamp(),
      ...(editingId ? {} : { createdAt: serverTimestamp() }),
    };

    try {
      if (editingId) {
        const existing = shifts.find((s) => s.id === editingId);
        if (existing?.invoiced) {
          showToast('error', 'This shift is already invoiced. Adjust via invoices instead.');
          return;
        }

        await updateItem(currentUser.uid, 'timeEntries', editingId, shiftData);
        setShifts((prev) => (prev || []).map((s) => (s.id === editingId ? { ...s, ...shiftData } : s)));
        showToast('success', 'Shift updated.');
      } else {
        const added = await addItem(currentUser.uid, 'timeEntries', shiftData);

        const inRange =
          rangeMode === 'all' ||
          (String(added.date || date) >= String(rangeFrom) && String(added.date || date) < String(rangeTo));

        if (inRange) setShifts((prev) => [{ ...added, ...shiftData }, ...(prev || [])]);
        showToast('success', 'Shift saved.');
      }

      resetForm();
    } catch (error) {
      showToast('error', 'Failed to save shift: ' + error.message);
    }
  };

  const handleEdit = (shift) => {
    if (isLocked) {
      showToast('info', 'Preview mode — actions are locked.');
      return;
    }

    setParticipantId(shift.participantId);
    setDate(shift.date);
    setStartTime(shift.startTime);
    setEndTime(shift.endTime);
    setNotes(shift.notes || '');

    setSupportItemCode(shift.supportItemCode || '');
    setUnitPrice(shift.unitPrice ?? '');

    setTravelKm(shift.travelKm ?? '');
    setTravelRatePerKm(shift.travelRatePerKm ?? '');

    setEditingId(shift.id);
    setShowAutofills(false);
    setTemplateId('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (isLocked) {
      showToast('info', 'Preview mode — actions are locked.');
      return;
    }

    const shift = shifts.find((s) => s.id === id);
    if (shift?.invoiced) {
      showToast('warning', 'This shift is linked to an invoice. Delete/adjust via invoices.');
      return;
    }

    if (!window.confirm('Delete this shift report permanently?')) return;
    try {
      await deleteItem(currentUser.uid, 'timeEntries', id);
      setShifts((prev) => (prev || []).filter((s) => s.id !== id));
      setSelectedShiftIds((prev) => prev.filter((x) => x !== id));
      showToast('success', 'Shift deleted.');
    } catch (error) {
      showToast('error', 'Delete failed: ' + error.message);
    }
  };

  const handleDuplicateShift = async (shift) => {
    if (isLocked) {
      showToast('info', 'Preview mode — actions are locked.');
      return;
    }

    if (!shift) return;
    if (shift.invoiced) {
      showToast('warning', 'This shift is invoiced. Duplicate a draft shift instead.');
      return;
    }

    const participant = participants.find((p) => p.id === shift.participantId);

    const newShiftData = {
      participantId: shift.participantId,
      date: todayISO(),
      startTime: shift.startTime || '',
      endTime: shift.endTime || '',
      hours: shift.hours || '0.00',
      notes: shift.notes || '',

      supportItemCode: shift.supportItemCode || '',
      unitPrice: shift.unitPrice ?? null,
      travelKm: shift.travelKm ?? null,
      travelRatePerKm: shift.travelRatePerKm ?? null,

      invoiced: false,
      invoiceId: null,
      invoiceNumber: null,

      participantSnapshot: {
        name: shift.participantSnapshot?.name || participant?.name || 'Unknown',
        ndisNumber: shift.participantSnapshot?.ndisNumber || participant?.ndisNumber || 'Unknown',
      },

      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      const added = await addItem(currentUser.uid, 'timeEntries', newShiftData);

      const inRange =
        rangeMode === 'all' ||
        (String(added.date || newShiftData.date) >= String(rangeFrom) &&
          String(added.date || newShiftData.date) < String(rangeTo));

      if (inRange) setShifts((prev) => [{ ...added, ...newShiftData }, ...(prev || [])]);

      showToast('success', 'Duplicated shift into a new draft (today).');
    } catch (e) {
      showToast('error', 'Duplicate failed: ' + e.message);
    }
  };

  // ---- Shift Report PDF (allowed in preview) ----
  const generateShiftPDF = (shift) => {
    const participant = participants.find((p) => p.id === shift.participantId);

    const pName = shift.participantSnapshot?.name || participant?.name || 'Unknown';
    const pNdis = shift.participantSnapshot?.ndisNumber || participant?.ndisNumber || 'Unknown';

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 14;

    const safe = (v) => (v ? String(v) : '');
    const wrap = (text, w) => doc.splitTextToSize(String(text || ''), w);

    doc.setDrawColor(0);
    doc.setLineWidth(0.3);
    doc.line(margin, 10, pageW - margin, 10);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('DAILY SHIFT REPORT', margin, 22);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80);
    doc.text('This document records supports delivered during a shift (not an invoice).', margin, 28);
    doc.setTextColor(0);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Date', pageW - margin, 18, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(shift.date), pageW - margin, 23, { align: 'right' });

    doc.setFont('helvetica', 'bold');
    doc.text('Duration', pageW - margin, 30, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.text(`${shift.startTime} – ${shift.endTime} (${safe(shift.hours)} hrs)`, pageW - margin, 35, {
      align: 'right',
    });

    let y = 46;

    const sectionTitle = (t) => {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(t, margin, y);
      doc.setDrawColor(200);
      doc.setLineWidth(0.4);
      doc.line(margin, y + 2, pageW - margin, y + 2);
      doc.setDrawColor(0);
      doc.setLineWidth(0.2);
      y += 10;
    };

    sectionTitle('Participant');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`Name: ${pName}`, margin, y);
    y += 6;
    doc.text(`NDIS Number: ${pNdis}`, margin, y);
    y += 10;

    sectionTitle('Shift Notes');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const noteLines = wrap(shift.notes || '', pageW - margin * 2);

    noteLines.forEach((ln) => {
      if (y > pageH - 20) {
        doc.addPage();
        y = 18;
      }
      doc.text(ln, margin, y);
      y += 5;
    });

    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(`Generated: ${formatDate(todayISO())}`, margin, pageH - 10);
    doc.setTextColor(0);

    const safeName = String(pName || 'participant').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    doc.save(`shift-report-${safeName}-${shift.date}.pdf`);
  };

  // ==============================
  // Sessions → Invoice (Merged lines)
  // ==============================
  const toggleSelectShift = (id) => {
    if (isLocked) {
      showToast('info', 'Preview mode — actions are locked.');
      return;
    }
    setSelectedShiftIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const clearSelection = () => setSelectedShiftIds([]);

  const saveNextInvoiceId = async (newId) => {
    const next = { ...(settings || {}), nextInvoiceId: newId, updatedAt: serverTimestamp() };
    setSettings(next);
    await saveUserSingleDoc(currentUser.uid, 'settings', next);
  };

  const mergeInvoiceLines = (items) => {
    const map = new Map();

    items.forEach((it) => {
      const dateKey = it.date || '';
      const codeKey = it.code || '';
      const amtKey = Number(it.amount || 0).toFixed(4);
      const key = `${dateKey}||${codeKey}||${amtKey}`;

      if (!map.has(key)) {
        map.set(key, {
          code: it.code,
          amount: Number(it.amount || 0),
          units: Number(it.units || 0),
          date: it.date,
          sourceShiftIds: it.sourceShiftId ? [it.sourceShiftId] : [],
        });
      } else {
        const existing = map.get(key);
        existing.units = Number(existing.units || 0) + Number(it.units || 0);
        if (it.sourceShiftId) existing.sourceShiftIds.push(it.sourceShiftId);
      }
    });

    return Array.from(map.values()).map((x) => ({
      code: x.code,
      amount: x.amount,
      units: Number(Number(x.units).toFixed(2)),
      date: x.date,
      sourceShiftIds: x.sourceShiftIds,
    }));
  };

  const createInvoiceFromSelected = async () => {
    if (isLocked) {
      showToast('info', 'Preview mode — actions are locked.');
      return;
    }

    const selected = (shifts || []).filter((s) => selectedShiftIds.includes(s.id));
    if (selected.length === 0) return;

    // ✅ must be one participant
    const pid = selected[0].participantId;
    if (selected.some((s) => s.participantId !== pid)) {
      showToast('warning', 'Selected shifts must be for the same participant.');
      return;
    }

    // ✅ never invoice across month (hard enforcement)
    const monthKey = getMonthKey(selected[0].date);
    if (selected.some((s) => getMonthKey(s.date) !== monthKey)) {
      showToast('warning', 'Selected shifts must all be in the same month.');
      return;
    }

    if (selected.some((s) => s.invoiced)) {
      showToast('warning', 'One or more selected shifts are already invoiced.');
      return;
    }

    const missingBilling = selected.filter((s) => !s.supportItemCode || s.unitPrice == null || s.unitPrice === '');
    if (missingBilling.length > 0) {
      showToast('warning', 'Some selected shifts are missing a Support Item Code or Unit Price.');
      return;
    }

    const missingTravelRate = selected.filter(
      (s) => Number(s.travelKm || 0) > 0 && !(Number(s.travelRatePerKm || 0) > 0)
    );
    if (missingTravelRate.length > 0) {
      showToast('warning', 'Some selected shifts have Travel KM but are missing a valid travel rate.');
      return;
    }

    const participant = participants.find((p) => p.id === pid);

    const providerSnapshot = {
      name: userInfo?.name || '',
      abn: userInfo?.abn || '',
      ndisProviderNumber: userInfo?.ndisProviderNumber || '',
      address: userInfo?.address || '',
      bankDetails: userInfo?.bankDetails || '',
    };

    const rawItems = [];
    selected.forEach((s) => {
      rawItems.push({
        code: s.supportItemCode,
        amount: Number(s.unitPrice || 0),
        units: Number(s.hours || 0),
        date: s.date,
        sourceShiftId: s.id,
      });

      const km = Number(s.travelKm || 0);
      const rate = Number(s.travelRatePerKm || 0);
      if (km > 0 && rate > 0) {
        rawItems.push({
          code: TRAVEL_CODE,
          amount: rate,
          units: km,
          date: s.date,
          sourceShiftId: s.id,
        });
      }
    });

    const billingItems = mergeInvoiceLines(rawItems);
    const totalAmount = billingItems.reduce((sum, i) => sum + Number(i.amount || 0) * Number(i.units || 0), 0);

    const invoiceNumber = settings?.nextInvoiceId || 1;

    const invoiceData = {
      participantId: pid,
      billingItems,
      totalAmount,
      date: todayISO(),
      dueDate: calculateDueDateISO(),
      invoiceNumber,
      participantSnapshot: {
        name: participant?.name || 'Unknown',
        ndisNumber: participant?.ndisNumber || 'Unknown',
      },
      providerSnapshot,
      paid: false,
      paidAt: null,
      source: 'sessions',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      const addedInvoice = await addItem(currentUser.uid, 'invoices', invoiceData);

      for (const s of selected) {
        await updateItem(currentUser.uid, 'timeEntries', s.id, {
          invoiced: true,
          invoiceId: addedInvoice.id,
          invoiceNumber,
          updatedAt: serverTimestamp(),
        });
      }

      setShifts((prev) =>
        (prev || []).map((s) =>
          selectedShiftIds.includes(s.id)
            ? { ...s, invoiced: true, invoiceId: addedInvoice.id, invoiceNumber }
            : s
        )
      );

      await saveNextInvoiceId(invoiceNumber + 1);

      setSelectedShiftIds([]);
      showToast('success', `Invoice INV-${String(invoiceNumber).padStart(4, '0')} created from selected shifts.`);
    } catch (e) {
      showToast('error', 'Failed to create invoice: ' + e.message);
    }
  };

  const canSave = participantId && date && startTime && endTime && notes.trim().length > 0 && hoursValue > 0;

  // ---------- Derived lists for UI ----------
  const filteredShifts = useMemo(() => {
    return (shifts || []).filter((s) => {
      const isDraft = !s.invoiced;
      const isInv = !!s.invoiced;

      if (!showDrafts && isDraft) return false;
      if (!showInvoiced && isInv) return false;

      if (filterParticipantId && s.participantId !== filterParticipantId) return false;

      return true;
    });
  }, [shifts, showDrafts, showInvoiced, filterParticipantId]);

  const visibleDraftIds = useMemo(() => filteredShifts.filter((s) => !s.invoiced).map((s) => s.id), [filteredShifts]);

  const stats = useMemo(() => {
    const list = shifts || [];
    const drafts = list.filter((s) => !s.invoiced).length;
    const invoiced = list.filter((s) => !!s.invoiced).length;
    return { loaded: list.length, drafts, invoiced };
  }, [shifts]);

  // ---- Grouped view: Participant -> Date -> Shifts ----
  const groupedShifts = useMemo(() => {
    const pName = (pid) => {
      const p = participants.find((x) => x.id === pid);
      return (p?.name || '').trim() || 'Unknown';
    };

    const byParticipant = new Map();

    (filteredShifts || []).forEach((s) => {
      const pid = s.participantId || 'unknown';
      if (!byParticipant.has(pid)) byParticipant.set(pid, []);
      byParticipant.get(pid).push(s);
    });

    const participantEntries = Array.from(byParticipant.entries()).sort((a, b) =>
      pName(a[0]).localeCompare(pName(b[0]))
    );

    return participantEntries.map(([pid, list]) => {
      const byDate = new Map();
      list.forEach((s) => {
        const d = String(s.date || '');
        if (!byDate.has(d)) byDate.set(d, []);
        byDate.get(d).push(s);
      });

      const dateEntries = Array.from(byDate.entries()).sort((a, b) => String(b[0]).localeCompare(String(a[0])));
      const dates = dateEntries.map(([d, arr]) => ({
        date: d,
        shifts: (arr || []).slice().sort((x, y) => String(x.startTime || '').localeCompare(String(y.startTime || ''))),
      }));

      return {
        participantId: pid,
        participantName: pName(pid),
        dates,
        count: list.length,
      };
    });
  }, [filteredShifts, participants]);

  // ---- Templates helpers ----
  const openSaveTemplateDialog = () => {
    runIfUnlocked(() => {
      const p = participants.find((x) => x.id === participantId);
      const base = p?.name ? `${p.name} template` : 'New template';
      setTemplateName(base);
      setOpenSaveTemplate(true);
    }, 'Preview mode — actions are locked.');
  };

  const saveTemplate = async () => {
    if (isLocked) {
      showToast('info', 'Preview mode — actions are locked.');
      return;
    }

    const name = String(templateName || '').trim();
    if (!name) {
      showToast('warning', 'Please enter a template name.');
      return;
    }

    const payload = {
      name,
      participantId: participantId || '',
      startTime: startTime || '',
      endTime: endTime || '',
      notes: notes || '',
      supportItemCode: supportItemCode || '',
      unitPrice: unitPrice !== '' ? Number(unitPrice) : null,
      travelKm: travelKm !== '' ? Number(travelKm) : null,
      travelRatePerKm: travelRatePerKm !== '' ? Number(travelRatePerKm) : null,
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
    };

    try {
      const added = await addItem(currentUser.uid, TEMPLATE_COLLECTION, payload);
      const next = [...(templates || []), added].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setTemplates(next);
      setOpenSaveTemplate(false);
      setTemplateName('');
      showToast('success', 'Template saved.');
    } catch (e) {
      showToast('error', 'Failed to save template: ' + e.message);
    }
  };

  const applyTemplate = (tid) => {
    runIfUnlocked(() => {
      setTemplateId(tid || '');
      const tpl = (templates || []).find((t) => t.id === tid);
      if (!tpl) return;

      setParticipantId(tpl.participantId || participantId || '');
      if (tpl.startTime) setStartTime(tpl.startTime);
      if (tpl.endTime) setEndTime(tpl.endTime);
      if (tpl.notes != null) setNotes(String(tpl.notes || ''));

      setSupportItemCode(tpl.supportItemCode || '');
      setUnitPrice(tpl.unitPrice ?? '');
      setTravelKm(tpl.travelKm ?? '');
      setTravelRatePerKm(tpl.travelRatePerKm ?? '');

      setEditingId(null);
      setShowAutofills(false);

      showToast('success', `Template applied: ${tpl.name}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 'Preview mode — actions are locked.');
  };

  const startRenameTemplate = (tpl) => {
    runIfUnlocked(() => {
      setEditingTemplateId(tpl?.id || null);
      setEditingTemplateName(tpl?.name || '');
    }, 'Preview mode — actions are locked.');
  };

  const saveRenamedTemplate = async () => {
    if (isLocked) {
      showToast('info', 'Preview mode — actions are locked.');
      return;
    }

    const id = editingTemplateId;
    const name = String(editingTemplateName || '').trim();
    if (!id) return;
    if (!name) {
      showToast('warning', 'Template name cannot be empty.');
      return;
    }

    try {
      await updateItem(currentUser.uid, TEMPLATE_COLLECTION, id, { name, updatedAt: serverTimestamp() });
      setTemplates((prev) => (prev || []).map((t) => (t.id === id ? { ...t, name } : t)));
      setEditingTemplateId(null);
      setEditingTemplateName('');
      showToast('success', 'Template updated.');
    } catch (e) {
      showToast('error', 'Failed to update template: ' + e.message);
    }
  };

  const removeTemplate = async (tpl) => {
    if (isLocked) {
      showToast('info', 'Preview mode — actions are locked.');
      return;
    }

    if (!tpl?.id) return;
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;

    try {
      await deleteItem(currentUser.uid, TEMPLATE_COLLECTION, tpl.id);
      setTemplates((prev) => (prev || []).filter((t) => t.id !== tpl.id));
      if (templateId === tpl.id) setTemplateId('');
      showToast('success', 'Template deleted.');
    } catch (e) {
      showToast('error', 'Failed to delete template: ' + e.message);
    }
  };

  // ---- Dialog style fix (forces white paper + black text) ----
  const dialogPaperProps = {
    sx: {
      bgcolor: '#fff',
      color: '#000',
      borderRadius: 3,
      border: '1px solid rgba(0,0,0,0.12)',
      '& .MuiTypography-root': { color: '#000' },
      '& .MuiDialogContent-root': { color: '#000' },
      '& .MuiInputBase-root': { color: '#000' },
      '& .MuiInputLabel-root': { color: 'rgba(0,0,0,0.7)' },
    },
  };

  if (loadingBase || accessLoading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: '#000' }}>
        <CircularProgress size={22} />
        <Typography sx={{ mt: 1, fontWeight: 900 }}>Loading…</Typography>
      </Box>
    );
  }

  const selectAllVisibleDrafts = () => {
    if (isLocked) {
      showToast('info', 'Preview mode — actions are locked.');
      return;
    }
    setSelectedShiftIds((prev) => {
      const set = new Set(prev);
      visibleDraftIds.forEach((id) => set.add(id));
      return Array.from(set);
    });
  };

  return (
    <Box
  sx={{
    p: { xs: 2, sm: 3, md: 4 },
    maxWidth: { md: '1200px' },
    mx: 'auto',
    width: '100%',
    overflowX: 'hidden',
    color: '#000',
  }}
>

      {/* Header */}
      <Box
        sx={{
          mb: 2.5,
          p: 2.5,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          color: '#000',
          background:
            'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(245,247,255,1) 45%, rgba(245,255,248,1) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
          <SubtleButton onClick={() => navigate('/')} startIcon={<ArrowBackIcon />}>
            Back
          </SubtleButton>

          <Box sx={{ flex: 1, minWidth: 260 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.1, color: '#000' }}
              >
                Time Tracking
              </Typography>

              {isLocked && (
                <Chip
                  label="Preview mode — actions locked"
                  sx={{
                    borderRadius: 999,
                    fontWeight: 900,
                    bgcolor: 'rgba(0,0,0,0.08)',
                    color: '#000',
                  }}
                />
              )}
            </Box>

            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)', mt: 0.5 }}>
              Log shifts as <strong>Daily Shift Reports</strong>. Optionally convert selected draft shifts into an invoice.
            </Typography>

            {/* Compact stats row */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Chip label={`Loaded: ${stats.loaded}`} sx={{ fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)', color: '#000' }} />
              <Chip label={`Draft: ${stats.drafts}`} sx={{ fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)', color: '#000' }} />
              <Chip label={`Invoiced: ${stats.invoiced}`} sx={{ fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)', color: '#000' }} />
            </Box>

            {/* Range row */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.2, alignItems: 'center' }}>
              <Chip icon={<CalendarMonthIcon />} label={rangeChipLabel} sx={{ borderRadius: 999, fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)' }} />

              <TinyButton
                variant={rangeMode === 'month' ? 'contained' : 'outlined'}
                onClick={setThisMonth}
                sx={{
                  bgcolor: rangeMode === 'month' ? '#000' : 'transparent',
                  color: rangeMode === 'month' ? '#fff' : '#000',
                  '&:hover': { bgcolor: rangeMode === 'month' ? '#222' : '#fafafa' },
                }}
              >
                This month
              </TinyButton>

              <TinyButton
                variant={rangeMode === 'threeMonths' ? 'contained' : 'outlined'}
                onClick={setLast3Months}
                sx={{
                  bgcolor: rangeMode === 'threeMonths' ? '#000' : 'transparent',
                  color: rangeMode === 'threeMonths' ? '#fff' : '#000',
                  '&:hover': { bgcolor: rangeMode === 'threeMonths' ? '#222' : '#fafafa' },
                }}
              >
                Last 3 months
              </TinyButton>

              <TinyButton
                variant={rangeMode === 'all' ? 'contained' : 'outlined'}
                onClick={setAllTime}
                sx={{
                  bgcolor: rangeMode === 'all' ? '#000' : 'transparent',
                  color: rangeMode === 'all' ? '#fff' : '#000',
                  '&:hover': { bgcolor: rangeMode === 'all' ? '#222' : '#fafafa' },
                }}
              >
                All time
              </TinyButton>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 1) Log shift */}
      <SoftCard
        title={editingId ? 'Edit Daily Shift Report' : 'Log Daily Shift Report'}
        subtitle="This is your shift note record. It is NOT an invoice."
        icon={<AccessTimeIcon fontSize="small" />}
        right={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              label={`Hours: ${calculateHours()}`}
              sx={{ fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 999, color: '#000' }}
            />
            <Tooltip title={isLocked ? 'Preview mode — start a trial to edit codes' : 'Manage your billing codes'}>
              <span>
                <SubtleButton
                  onClick={openCodes}
                  startIcon={<SettingsIcon />}
                  disabled={isLocked || !codesLoaded}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Billing codes
                </SubtleButton>
              </span>
            </Tooltip>
          </Box>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Templates row */}
<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, alignItems: 'center' }}>
  {isMobile ? (
    <Autocomplete
      value={(templates || []).find((t) => t.id === templateId) || null}
      onChange={(_, newVal) => applyTemplate(newVal?.id || '')}
      options={templates || []}
      disablePortal
      PopperComponent={MobilePopper}
      autoHighlight
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      getOptionLabel={(opt) => String(opt?.name || '')}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Template (optional)"
          placeholder="Search templates…"
          fullWidth
          disabled={isLocked}
          InputProps={{
            ...params.InputProps,
            sx: { fontSize: 16 },
          }}
          inputProps={{
            ...params.inputProps,
            style: { fontSize: 16 },
          }}
        />
      )}
      sx={{ width: { xs: '100%', sm: 360 } }}
    />
  ) : (
    <StyledSelect
      value={templateId}
      onChange={(e) => applyTemplate(e.target.value)}
      displayEmpty
      sx={{ width: { xs: '100%', sm: 360 } }}
      MenuProps={menuProps}
      disabled={isLocked}
    >
      <MenuItem value="">
        <em>Templates (optional)</em>
      </MenuItem>
      {(templates || []).map((t) => (
        <MenuItem key={t.id} value={t.id}>
          {t.name}
        </MenuItem>
      ))}
    </StyledSelect>
  )}

  <SubtleButton
    onClick={openSaveTemplateDialog}
    startIcon={<BookmarksIcon />}
    disabled={isLocked || (!participantId && !notes.trim())}
    sx={{ whiteSpace: 'nowrap' }}
  >
    Save as template
  </SubtleButton>

  <SubtleButton
    onClick={() => runIfUnlocked(() => setOpenManageTemplates(true))}
    startIcon={<EditIcon />}
    disabled={isLocked || (templates || []).length === 0}
    sx={{ whiteSpace: 'nowrap' }}
  >
    Manage templates
  </SubtleButton>

  <SubtleButton
    onClick={() => runIfUnlocked(copyLastShiftToForm)}
    startIcon={<ReplayIcon />}
    sx={{ whiteSpace: 'nowrap' }}
    disabled={isLocked}
  >
    Copy last shift
  </SubtleButton>
</Box>


          <Divider />

          <Box
  sx={{
    display: { xs: 'grid', sm: 'flex' },         // ✅ mobile grid, desktop flex (unchanged)
    gridTemplateColumns: { xs: '1fr', sm: 'none' },
    flexWrap: { sm: 'wrap' },
    gap: 1.5,
    alignItems: 'center',
  }}
>

            {isMobile ? (
  <Autocomplete
    value={activeParticipants.find((p) => p.id === participantId) || null}
    onChange={(_, newVal) => setParticipantId(newVal?.id || '')}
    options={activeParticipants}
    disablePortal
    PopperComponent={MobilePopper}
    autoHighlight
    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
    getOptionLabel={(opt) => String(opt?.name || '')}
    renderInput={(params) => (
      <TextField
        {...params}
        label="Participant"
        placeholder="Search participant…"
        fullWidth
        disabled={isLocked}
      />
    )}
    renderOption={(props, opt) => (
      <li {...props} key={opt.id}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontWeight: 900 }}>{opt.name}</Typography>
          {!!opt.ndisNumber && (
            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.65)' }}>
              NDIS: {opt.ndisNumber}
            </Typography>
          )}
        </Box>
      </li>
    )}
  />
) : (
  <StyledSelect
    value={participantId}
    onChange={(e) => setParticipantId(e.target.value)}
    displayEmpty
    sx={{ width: { xs: '100%', sm: 360 } }}
    MenuProps={menuProps}
    disabled={isLocked}
  >
    <MenuItem value="">
      <em>Select participant</em>
    </MenuItem>
    {activeParticipants.map((p) => (
      <MenuItem key={p.id} value={p.id}>
        {p.name}
      </MenuItem>
    ))}
  </StyledSelect>
)}


            <TextField
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 180 } }}
              disabled={isLocked}
            />

           {isMobile ? (
  <Autocomplete
    value={startTime ? { id: startTime, label: startTime } : null}
    onChange={(_, newVal) => setStartTime(newVal?.id || '')}
    options={TIME_OPTIONS_5.map((t) => ({ id: t, label: t }))}
    disablePortal
    PopperComponent={MobilePopper}
    autoHighlight
    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
    getOptionLabel={(opt) => String(opt?.label || '')}
    renderInput={(params) => (
      <TextField
        {...params}
        label="Start time"
        placeholder="Select…"
        fullWidth
        disabled={isLocked}
        InputProps={{
          ...params.InputProps,
          sx: { fontSize: 16 }, // ✅ stops iPhone focus zoom
        }}
        inputProps={{
          ...params.inputProps,
          style: { fontSize: 16 },
        }}
      />
    )}
    sx={{ width: '100%' }}
  />
) : (
  <StyledSelect
    value={startTime}
    onChange={(e) => setStartTime(e.target.value)}
    displayEmpty
    sx={{ width: { xs: '100%', sm: 170 } }}
    MenuProps={menuProps}
    disabled={isLocked}
  >
    <MenuItem value="">
      <em>Start time</em>
    </MenuItem>
    {TIME_OPTIONS_5.map((t) => (
      <MenuItem key={t} value={t}>
        {t}
      </MenuItem>
    ))}
  </StyledSelect>
)}


            {isMobile ? (
  <Autocomplete
    value={endTime ? { id: endTime, label: endTime } : null}
    onChange={(_, newVal) => setEndTime(newVal?.id || '')}
    options={TIME_OPTIONS_5.map((t) => ({ id: t, label: t }))}
    disablePortal
    PopperComponent={MobilePopper}
    autoHighlight
    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
    getOptionLabel={(opt) => String(opt?.label || '')}
    renderInput={(params) => (
      <TextField
        {...params}
        label="End time"
        placeholder="Select…"
        fullWidth
        disabled={isLocked}
        InputProps={{
          ...params.InputProps,
          sx: { fontSize: 16 },
        }}
        inputProps={{
          ...params.inputProps,
          style: { fontSize: 16 },
        }}
      />
    )}
    sx={{ width: '100%' }}
  />
) : (
  <StyledSelect
    value={endTime}
    onChange={(e) => setEndTime(e.target.value)}
    displayEmpty
    sx={{ width: { xs: '100%', sm: 170 } }}
    MenuProps={menuProps}
    disabled={isLocked}
  >
    <MenuItem value="">
      <em>End time</em>
    </MenuItem>
    {TIME_OPTIONS_5.map((t) => (
      <MenuItem key={t} value={t}>
        {t}
      </MenuItem>
    ))}
  </StyledSelect>
)}

          </Box>

          <Divider />

          <Box>
            <Typography sx={{ fontWeight: 900, mb: 0.5, color: '#000' }}>Shift Notes / Summary</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)', mb: 1 }}>
              Write what supports were delivered, outcomes, risks/incidents, and any handover.
            </Typography>

            <TextField
  multiline
  rows={9}
  value={notes}
  onChange={(e) => {
    setNotes(e.target.value);
    // keep selection fresh while typing
    notesSelRef.current = {
      start: e.target.selectionStart ?? 0,
      end: e.target.selectionEnd ?? 0,
    };
  }}
  fullWidth
  placeholder="Example: Supported participant with meal prep and community access. Participant engaged well…"
  disabled={isLocked}
  inputRef={notesRef}
  onClick={captureNotesSelection}
  onKeyUp={captureNotesSelection}
  onSelect={captureNotesSelection}
  onFocus={captureNotesSelection}
/>


            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
              <TinyButton
                variant="outlined"
                onClick={() => runIfUnlocked(insertTemplateHeader)}
                startIcon={<DescriptionIcon />}
                disabled={isLocked}
              >
                Insert template
              </TinyButton>

              <TinyButton
                variant={showAutofills ? 'contained' : 'outlined'}
                onClick={() => runIfUnlocked(() => setShowAutofills((v) => !v))}
                startIcon={<AutoAwesomeIcon />}
                disabled={isLocked}
                sx={{
                  bgcolor: showAutofills ? '#000' : 'transparent',
                  color: showAutofills ? '#fff' : '#000',
                  '&:hover': { bgcolor: showAutofills ? '#222' : '#fafafa' },
                }}
              >
                {showAutofills ? 'Hide autofills' : 'Show autofills'}
              </TinyButton>

              <TinyButton
                variant="outlined"
                color="error"
                onClick={() => runIfUnlocked(clearNotes)}
                startIcon={<ClearIcon />}
                disabled={isLocked}
              >
                Clear notes
              </TinyButton>
            </Box>

            {showAutofills && (
              <Paper
                elevation={0}
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.08)',
                  bgcolor: '#fafafa',
                  color: '#000',
                }}
              >
                <Typography sx={{ fontWeight: 900, mb: 1, color: '#000' }}>Quick Autofill (click to add)</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {autofillOptions.map((text, i) => (
                    <TinyButton
                      key={i}
                      size="small"
                      variant="outlined"
                      onClick={() => runIfUnlocked(() => autofillNotes(text))}
                      disabled={isLocked}
                      sx={{ bgcolor: 'white', color: '#000' }}
                    >
                      {autofillLabels[i] || `Option ${i + 1}`}
                    </TinyButton>
                  ))}
                </Box>
              </Paper>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography sx={{ fontWeight: 900, mb: 0.5, color: '#000' }}>Billing (optional)</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)', mb: 1.5 }}>
              Only fill this out if you want to create invoices from shifts later. If you don’t invoice from sessions, you can leave this blank.
            </Typography>

            <Box
  sx={{
    display: { xs: 'grid', md: 'flex' },   // ✅ mobile stacks, md+ stays your current flex row
    gridTemplateColumns: { xs: '1fr', md: 'none' },
    flexWrap: { md: 'wrap' },
    gap: 1.5,
    alignItems: 'center',
  }}
>

              {/* ✅ EXACT invoices-style billing code picker */}

<Autocomplete
  value={selectedSupportCodeObj}
  onChange={(_, newVal) => onSelectSupportCodeObj(newVal)}
  options={supportCodeOptions}
  disablePortal
  PopperComponent={MobilePopper}
  autoHighlight
  blurOnSelect
  isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
  getOptionLabel={(opt) =>
    opt ? `${opt.code}${opt.description ? ` — ${opt.description}` : ''}` : ''
  }
  ListboxProps={{
    style: {
      maxHeight: 360,
      overflow: 'auto',
    },
  }}
  PaperComponent={(props) => (
    <Paper
      {...props}
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.12)',
        bgcolor: '#fff',
        color: '#000',
        boxShadow: '0 18px 45px rgba(0,0,0,0.12)',
      }}
    />
  )}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Support item code"
      placeholder="Search code or description…"
      fullWidth
      sx={{ width: { xs: '100%', md: 520 } }}
    />
  )}
  renderOption={(props, opt) => (
    <li {...props} key={opt.id}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', width: '100%' }}>
        {opt.favorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
            {opt.code}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: 'rgba(0,0,0,0.65)',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: 1.25,
            }}
          >
            {opt.description || '—'}
            {opt.defaultRate ? ` • Default $${Number(opt.defaultRate).toFixed(2)}` : ''}
          </Typography>
        </Box>
      </Box>
    </li>
  )}
/>


              <TextField
                label="Unit price"
                type="number"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                sx={{ width: { xs: '100%', md: 160 } }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                disabled={isLocked || !supportItemCode}
              />

              <TextField
                label="Travel KM (optional)"
                type="number"
                value={travelKm}
                onChange={(e) => {
                  setTravelKm(e.target.value);
                  if (Number(e.target.value || 0) > 0) onSetTravelDefaultsIfAvailable();
                }}
                sx={{ width: { xs: '100%', md: 180 } }}
                disabled={isLocked}
              />

              <TextField
                label="Travel rate ($/km)"
                type="number"
                value={travelRatePerKm}
                onChange={(e) => setTravelRatePerKm(e.target.value)}
                sx={{ width: { xs: '100%', md: 200 } }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                disabled={isLocked}
              />
            </Box>

            {/* helpful hint row */}
            <Box sx={{ mt: 1.2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                size="small"
                label={`⭐ Favorites first`}
                sx={{ fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)' }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                Tip: set a Default rate in Billing codes to auto-fill Unit price.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.2, flexWrap: 'wrap', mt: 1 }}>
            <PrimaryButton onClick={handleSave} disabled={isLocked || !canSave}>
              {editingId ? 'Update shift report' : 'Save shift report'}
            </PrimaryButton>

            {editingId && (
              <SubtleButton onClick={() => runIfUnlocked(resetForm)} disabled={isLocked}>
                Cancel
              </SubtleButton>
            )}
          </Box>
        </Box>
      </SoftCard>

      {/* 2) Invoice builder */}
      <SoftCard
        title="Invoice from Shifts (optional)"
        subtitle="Select draft shifts below to create ONE invoice. This does not affect your shift notes — it only links them to an invoice."
        icon={<ReceiptLongIcon fontSize="small" />}
        right={
          <Chip
            label={`Selected: ${selectedShiftIds.length}`}
            sx={{ fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)', borderRadius: 999, color: '#000' }}
          />
        }
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <SubtleButton onClick={selectAllVisibleDrafts} disabled={isLocked || visibleDraftIds.length === 0}>
            Select visible drafts
          </SubtleButton>

          <SubtleButton onClick={clearSelection} disabled={selectedShiftIds.length === 0}>
            Clear selection
          </SubtleButton>

          <PrimaryButton onClick={createInvoiceFromSelected} disabled={isLocked || selectedShiftIds.length === 0}>
            Create invoice from selected shifts
          </PrimaryButton>

          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
            Tip: Don’t mix months (enforced).
          </Typography>
        </Box>
      </SoftCard>

      {/* 3) Saved shift reports list (Grouped) */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          bgcolor: '#fff',
          color: '#000',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 900, color: '#000' }}>
            Saved Shift Reports
          </Typography>

          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
            Grouped by participant + date.
          </Typography>

          <Box sx={{ flex: 1 }} />

          <FormControlLabel
            control={<Switch checked={showDrafts} onChange={(e) => setShowDrafts(e.target.checked)} />}
            label="Show drafts"
          />
          <FormControlLabel
            control={<Switch checked={showInvoiced} onChange={(e) => setShowInvoiced(e.target.checked)} />}
            label="Show invoiced"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.5, alignItems: 'center' }}>
          {isMobile ? (
  <Autocomplete
    value={activeParticipants.find((p) => p.id === filterParticipantId) || null}
    onChange={(_, newVal) => setFilterParticipantId(newVal?.id || '')}
    options={activeParticipants}
    disablePortal
    PopperComponent={MobilePopper}
    autoHighlight
    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
    getOptionLabel={(opt) => String(opt?.name || '')}
    renderInput={(params) => (
      <TextField
        {...params}
        label="Filter participant"
        placeholder="All participants"
        fullWidth
        InputProps={{
          ...params.InputProps,
          sx: { fontSize: 16 }, // ✅ prevents iPhone focus zoom
        }}
        inputProps={{
          ...params.inputProps,
          style: { fontSize: 16 },
        }}
      />
    )}
    renderOption={(props, opt) => (
      <li {...props} key={opt.id}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontWeight: 900 }}>{opt.name}</Typography>
          {!!opt.ndisNumber && (
            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.65)' }}>
              NDIS: {opt.ndisNumber}
            </Typography>
          )}
        </Box>
      </li>
    )}
    sx={{ width: '100%' }}
  />
) : (
  <StyledSelect
    value={filterParticipantId}
    onChange={(e) => setFilterParticipantId(e.target.value)}
    displayEmpty
    sx={{ width: { xs: '100%', sm: 320 } }}
    MenuProps={menuProps}
  >
    <MenuItem value="">
      <em>All participants</em>
    </MenuItem>
    {activeParticipants.map((p) => (
      <MenuItem key={p.id} value={p.id}>
        {p.name}
      </MenuItem>
    ))}
  </StyledSelect>
)}

        </Box>

        <Divider sx={{ my: 2 }} />

        {loadingShifts ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={22} />
            <Typography sx={{ mt: 1, fontWeight: 900, color: '#000' }}>Loading shift reports…</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
              {rangeMode === 'all' ? 'All time' : `From ${monthLabel(rangeFrom)}`}
            </Typography>
          </Box>
        ) : groupedShifts.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 900, color: '#000' }}>No shift reports found</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
              Try changing your filters or range.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {groupedShifts.map((group) => (
              <Paper
                key={group.participantId}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                  bgcolor: '#fff',
                  color: '#000',
                }}
              >
                <Box sx={{ p: 1.5, bgcolor: '#f6f6f6', color: '#000', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 900, color: '#000' }}>{group.participantName}</Typography>
                  <Chip
                    size="small"
                    label={`${group.count} shift${group.count === 1 ? '' : 's'}`}
                    sx={{ fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)', color: '#000' }}
                  />
                </Box>

                <Divider />

                {group.dates.map((d) => (
                  <Box key={`${group.participantId}-${d.date}`}>
                    <Box sx={{ px: 1.5, py: 1, bgcolor: '#ffffff', color: '#000', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 900, color: '#000' }}>{formatDate(d.date)}</Typography>
                      <Chip
                        size="small"
                        label={`${d.shifts.length} item${d.shifts.length === 1 ? '' : 's'}`}
                        sx={{ fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)', color: '#000' }}
                      />
                    </Box>

                    <TableContainer sx={{ maxHeight: { xs: 520, md: 'none' } }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            {[
                              { label: '', checkbox: true },
                              { label: 'Time' },
                              { label: 'Hours' },
                              { label: 'Notes (preview)' },
                              { label: 'Billing' },
                              { label: 'Status' },
                              { label: 'Actions', align: 'right' },
                            ].map((h, idx) => (
                              <TableCell
                                key={idx}
                                align={h.align || 'left'}
                                padding={h.checkbox ? 'checkbox' : 'normal'}
                                sx={{
                                  bgcolor: '#fafafa',
                                  color: '#000',
                                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                                }}
                              >
                                {h.label ? <strong>{h.label}</strong> : null}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {d.shifts.map((shift) => {
                            const isSelected = selectedShiftIds.includes(shift.id);
                            const isInvoiced = !!shift.invoiced;

                            const billingSummary = (() => {
                              const code = codeShort(shift.supportItemCode);
                              const price =
                                shift.unitPrice != null && shift.unitPrice !== '' ? money(shift.unitPrice) : '';
                              const km = Number(shift.travelKm || 0);
                              const rate = Number(shift.travelRatePerKm || 0);

                              const parts = [];
                              if (code) parts.push(`${code} @ ${price || '—'}`);
                              if (km > 0 && rate > 0) parts.push(`Travel: ${km}km @ ${money(rate)}/km`);
                              return parts.length ? parts.join(' • ') : '—';
                            })();

                            return (
                              <TableRow
                                key={shift.id}
                                hover
                                sx={{
                                  bgcolor: '#fff',
                                  color: '#000',
                                  '&:hover': { bgcolor: '#f2f2f2' },
                                }}
                              >
                                <TableCell padding="checkbox" sx={{ color: '#000' }}>
                                  <Checkbox
                                    checked={isSelected}
                                    disabled={isLocked || isInvoiced}
                                    onChange={() => toggleSelectShift(shift.id)}
                                  />
                                </TableCell>

                                <TableCell sx={{ whiteSpace: 'nowrap', color: '#000' }}>
                                  {shift.startTime} – {shift.endTime}
                                </TableCell>

                                <TableCell sx={{ color: '#000' }}>{shift.hours}</TableCell>

                                <TableCell sx={{ color: '#000' }}>
                                  <Typography variant="body2" sx={{ color: '#000' }}>
                                    {clampText(shift.notes, 95)}
                                  </Typography>
                                </TableCell>

                                <TableCell sx={{ color: '#000' }}>
                                  <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.75)' }}>
                                    {billingSummary}
                                  </Typography>
                                </TableCell>

                                <TableCell sx={{ color: '#000' }}>
                                  {isInvoiced ? (
                                    <Chip
                                      size="small"
                                      color="success"
                                      label={`Invoiced (INV-${String(shift.invoiceNumber || 0).padStart(4, '0')})`}
                                    />
                                  ) : (
                                    <Chip size="small" label="Draft (shift report)" />
                                  )}
                                </TableCell>

                                <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: '#000' }}>
                                  <Tooltip title={isLocked ? 'Preview mode (locked)' : 'Edit shift report'}>
                                    <span>
                                      <IconButton onClick={() => handleEdit(shift)} size="small" disabled={isLocked}>
                                        <EditIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>

                                  <Tooltip title={isLocked ? 'Preview mode (locked)' : 'Duplicate draft shift (creates new entry)'}>
                                    <span>
                                      <IconButton
                                        onClick={() => handleDuplicateShift(shift)}
                                        size="small"
                                        disabled={isLocked || isInvoiced}
                                      >
                                        <ContentCopyIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>

                                  <Tooltip title={isInvoiced ? 'Linked to invoice (cannot delete here)' : isLocked ? 'Preview mode (locked)' : 'Delete shift report'}>
                                    <span>
                                      <IconButton onClick={() => handleDelete(shift.id)} size="small" disabled={isLocked || isInvoiced}>
                                        <DeleteIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>

                                  <Tooltip title="Download shift report PDF">
                                    <span>
                                      <IconButton onClick={() => generateShiftPDF(shift)} size="small">
                                        <DownloadIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Divider />
                  </Box>
                ))}
              </Paper>
            ))}

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <PrimaryButton onClick={loadMoreShifts} disabled={loadingMore}>
                  {loadingMore ? 'Loading…' : 'Load more'}
                </PrimaryButton>
              </Box>
            )}

            {!hasMore && (shifts || []).length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Chip label="End of results" sx={{ borderRadius: 999, fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)' }} />
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Save template dialog */}
      <Dialog
        open={openSaveTemplate}
        onClose={() => setOpenSaveTemplate(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#000' }}>Save as Template</DialogTitle>
        <DialogContent sx={{ pt: 1, color: '#000' }}>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)', mb: 1 }}>
            Templates are saved per user in Firestore: <strong>users/{'{uid}'}/timeTemplates</strong>.
          </Typography>
          <TextField
            label="Template name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            fullWidth
            autoFocus
            disabled={isLocked}
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'rgba(0,0,0,0.65)' }}>
            Saved fields: participant (optional), times, notes, billing code, unit price, travel km + rate.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <SubtleButton onClick={() => setOpenSaveTemplate(false)}>Cancel</SubtleButton>
          <PrimaryButton onClick={saveTemplate} disabled={isLocked}>
            Save template
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Manage templates dialog */}
      <Dialog
        open={openManageTemplates}
        onClose={() => setOpenManageTemplates(false)}
        fullWidth
        maxWidth="md"
        PaperProps={dialogPaperProps}
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#000' }}>Manage Templates</DialogTitle>
        <DialogContent sx={{ pt: 1, color: '#000' }}>
          {(templates || []).length === 0 ? (
            <Typography sx={{ color: '#000' }}>No templates yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {(templates || []).map((t) => {
                const isRenaming = editingTemplateId === t.id;
                return (
                  <Paper
                    key={t.id}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      border: '1px solid rgba(0,0,0,0.08)',
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      bgcolor: '#fff',
                      color: '#000',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      {isRenaming ? (
                        <TextField
                          value={editingTemplateName}
                          onChange={(e) => setEditingTemplateName(e.target.value)}
                          size="small"
                          fullWidth
                          disabled={isLocked}
                        />
                      ) : (
                        <Typography sx={{ fontWeight: 900, color: '#000' }}>{t.name}</Typography>
                      )}
                      <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                        {t.participantId ? `Participant linked` : 'No participant linked'} • {t.startTime || '—'}–{t.endTime || '—'} •{' '}
                        {codeShort(t.supportItemCode) || 'No code'}
                      </Typography>
                    </Box>

                    {isRenaming ? (
                      <>
                        <SubtleButton
                          onClick={() => {
                            setEditingTemplateId(null);
                            setEditingTemplateName('');
                          }}
                        >
                          Cancel
                        </SubtleButton>
                        <PrimaryButton onClick={saveRenamedTemplate} disabled={isLocked}>
                          Save
                        </PrimaryButton>
                      </>
                    ) : (
                      <>
                        <SubtleButton onClick={() => applyTemplate(t.id)} disabled={isLocked}>
                          Use
                        </SubtleButton>
                        <SubtleButton onClick={() => startRenameTemplate(t)} startIcon={<EditIcon />} disabled={isLocked}>
                          Rename
                        </SubtleButton>
                        <SubtleButton onClick={() => removeTemplate(t)} startIcon={<DeleteIcon />} color="error" disabled={isLocked}>
                          Delete
                        </SubtleButton>
                      </>
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <SubtleButton onClick={() => setOpenManageTemplates(false)}>Close</SubtleButton>
        </DialogActions>
      </Dialog>

      {/* Billing Codes Dialog (same as Invoices.js) */}
      <Dialog open={codesOpen} onClose={() => setCodesOpen(false)} fullWidth maxWidth="md" PaperProps={dialogPaperProps}>
        <DialogTitle sx={{ fontWeight: 900 }}>My Billing Codes</DialogTitle>

        <DialogContent sx={{ pt: 1, bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <TextField
              value={codesQuery}
              onChange={(e) => setCodesQuery(e.target.value)}
              placeholder="Search code or description…"
              sx={{ flex: 1, minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <SubtleButton onClick={addCodeRow} startIcon={<AddIcon />}>
              Add code
            </SubtleButton>
          </Box>

          <Paper elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden' }}>
  <TableContainer sx={{ maxHeight: 520 }}>
    <Table
      stickyHeader
      sx={{
        tableLayout: 'fixed',
        '& .MuiTableCell-stickyHeader': {
          bgcolor: '#f7f7f7',
          zIndex: 3,
          borderBottom: '1px solid rgba(0,0,0,0.10)',
        },
        '& td, & th': {
          py: 1.25,
          verticalAlign: 'middle',
        },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 70, fontWeight: 900 }}>Fav</TableCell>
          <TableCell sx={{ width: 240, fontWeight: 900 }}>Billing code</TableCell>
          <TableCell sx={{ fontWeight: 900 }}>Description</TableCell>
          <TableCell sx={{ width: 160, fontWeight: 900 }}>Default rate</TableCell>
          <TableCell sx={{ width: 90, fontWeight: 900 }} align="right">
            Actions
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {filteredCodesDraft.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
              <Typography sx={{ fontWeight: 900 }}>No codes</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
                Add the few codes you actually use (most people use 5–15).
              </Typography>
            </TableCell>
          </TableRow>
        ) : (
          filteredCodesDraft.map((c) => (
            <TableRow key={c.id} hover sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
              <TableCell>
                <Tooltip title={c.favorite ? 'Unfavourite' : 'Favourite'}>
                  <IconButton size="small" onClick={() => updateCodeRow(c.id, { favorite: !c.favorite })}>
                    {c.favorite ? <StarIcon /> : <StarBorderIcon />}
                  </IconButton>
                </Tooltip>
              </TableCell>

              <TableCell>
                <TextField
                  value={c.code}
                  onChange={(e) => updateCodeRow(c.id, { code: e.target.value })}
                  placeholder="01_011_0107_1_1"
                  fullWidth
                  size="small"
                  inputProps={{ style: { fontSize: 14 } }}
                />
              </TableCell>

              <TableCell>
                <TextField
                  value={c.description}
                  onChange={(e) => updateCodeRow(c.id, { description: e.target.value })}
                  placeholder="Assistance With Self-Care Activities…"
                  fullWidth
                  size="small"
                  inputProps={{ style: { fontSize: 14 } }}
                />
              </TableCell>

              <TableCell>
                <TextField
                  value={c.defaultRate}
                  onChange={(e) => updateCodeRow(c.id, { defaultRate: e.target.value })}
                  type="number"
                  fullWidth
                  size="small"
                  inputProps={{ style: { fontSize: 14 } }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </TableCell>

              <TableCell align="right">
                <Tooltip title="Delete">
                  <IconButton size="small" color="error" onClick={() => deleteCodeRow(c.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  </TableContainer>
</Paper>


          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'rgba(0,0,0,0.65)' }}>
            Tip: Favourite codes appear at the top when selecting a support item code.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <SubtleButton onClick={() => setCodesOpen(false)}>Cancel</SubtleButton>
          <PrimaryButton onClick={saveCodesDialog}>Save codes</PrimaryButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={2600}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimeTracking;




